import React, { useState, useEffect } from 'react';
import {
  Search, Plus, Filter, Box, Zap, ChevronRight, Package, X, BarChart2
} from 'lucide-react';
import { useLang } from '../../core/lang.jsx';
import LangKeys from '../../core/locales/keys';
import itemService from '../../services/itemService';
import ProductDetailsView from './ProductDetailsView';
import ServiceDetailsView from './ServiceDetailsView';
import AddProductModal from './AddProductModal';
import AddServiceModal from './AddServiceModal';
import ItemReportView from './ItemReportView';

const ItemsHome = () => {
  const { t } = useLang();
  const [activeTab, setActiveTab] = useState('products');
  const [selectedItem, setSelectedItem] = useState(null);
  const [search, setSearch] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showFilterModal, setShowFilterModal] = useState(false);
  const [currentSort, setCurrentSort] = useState('latestFirst');
  const [productFilter, setProductFilter] = useState('all');

  const [showProductModal, setShowProductModal] = useState(false);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showReport, setShowReport] = useState(false);

  useEffect(() => {
    fetchItems();
  }, [activeTab]);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const data = await itemService.getAllItems();
      setItems(data);

      // If an item is currently selected, refresh its data from the new list
      if (selectedItem) {
        const updatedItem = data.find(i => i.id === selectedItem.id);
        if (updatedItem) setSelectedItem(updatedItem);
      }
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  const handleAddClick = () => {
    setEditingItem(null);
    if (activeTab === 'products') setShowProductModal(true);
    else setShowServiceModal(true);
  };

  const handleEditClick = (item) => {
    if (!item) return;
    setEditingItem(item);
    if (item.item_type === 'product' || item.item_type === 'item') {
      setShowProductModal(true);
    } else {
      setShowServiceModal(true);
    }
  };

  const handleDeleteItem = async (id) => {
    if (window.confirm(t(LangKeys.delete_confirmation) || 'Are you sure?')) {
      const success = await itemService.deleteItem(id);
      if (success) {
        setItems(prev => prev.filter(item => item.id !== id));
        setSelectedItem(null);
        fetchItems();
      }
    }
  };

  const handleSaveSuccess = () => {
    fetchItems();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency', currency: 'INR', maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const summary = {
    totalStockValue: items.filter(i => i.item_type === 'product').reduce((acc, curr) => acc + ((curr.opening_stock || 0) * (curr.sale_price || 0)), 0),
    lowStockCount: items.filter(i => i.item_type === 'product' && i.opening_stock <= (i.low_stock_alert || 2)).length,
  };

  const getProcessedItems = () => {
    let result = items.filter(item => item.item_type === (activeTab === 'products' ? 'product' : 'service'));
    if (search) result = result.filter(item => item.name.toLowerCase().includes(search.toLowerCase()));
    if (activeTab === 'products' && productFilter === 'lowStock') result = result.filter(item => item.opening_stock <= (item.low_stock_alert || 2));

    result.sort((a, b) => {
      if (currentSort === 'byNameAZ') return a.name.localeCompare(b.name);
      if (currentSort === 'byNameZA') return b.name.localeCompare(a.name);
      if (currentSort === 'latestFirst') return b.id - a.id;
      if (currentSort === 'oldestFirst') return a.id - b.id;
      return 0;
    });
    return result;
  };

  const filteredItems = getProcessedItems();

  if (showReport) {
    return <ItemReportView onBack={() => setShowReport(false)} />;
  }

  return (
    <div style={styles.container}>
      {/* LEFT COLUMN: LIST */}
      <div style={styles.leftCol}>
        <div style={styles.summarySection}>
          <div style={styles.summaryCard}>
            <span style={styles.summaryLabel}>{activeTab === 'products' ? t(LangKeys.total_stock) : t(LangKeys.net_monthly_sales)}</span>
            <span style={{ ...styles.summaryValue, color: '#16A34A' }}>{activeTab === 'products' ? formatCurrency(summary.totalStockValue) : '28 Units'}</span>
          </div>
          <div style={styles.summaryCard}>
            <span style={styles.summaryLabel}>{activeTab === 'products' ? t(LangKeys.low_stock_items) : t(LangKeys.net_monthly_sales) + ' (₹)'}</span>
            <span style={{ ...styles.summaryValue, color: '#DC2626' }}>{activeTab === 'products' ? `${summary.lowStockCount} Items` : formatCurrency(85000)}</span>
          </div>
        </div>

        <div style={styles.tabContainer}>
          <button style={{ ...styles.tabBtn, ...(activeTab === 'products' ? styles.activeTab : {}) }} onClick={() => { setActiveTab('products'); setSelectedItem(null); }}>
            <Box size={18} /> {t(LangKeys.products)}
          </button>
          <button style={{ ...styles.tabBtn, ...(activeTab === 'services' ? styles.activeTab : {}) }} onClick={() => { setActiveTab('services'); setSelectedItem(null); }}>
            <Zap size={18} /> {t(LangKeys.services)}
          </button>
        </div>

        <div style={styles.searchRow}>
          <div style={styles.searchBox}>
            <Search size={18} color="#94A3B8" />
            <input
              type="text"
              placeholder={activeTab === 'products' ? t(LangKeys.search_items) : t(LangKeys.search_services)}
              style={styles.searchInput}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <button style={{ ...styles.filterBtn, ...(productFilter !== 'all' ? styles.activeFilter : {}) }} onClick={() => setShowFilterModal(true)}>
            <Filter size={18} />
          </button>
          <button style={styles.reportBtn} onClick={() => setShowReport(true)} title={t(LangKeys.item_sales_report) || 'Reports'}>
            <BarChart2 size={18} />
          </button>
        </div>

        <div style={styles.listArea}>
          {loading ? (
            <div style={styles.loadingBox}><div className="premium-spinner"></div></div>
          ) : filteredItems.length > 0 ? (
            filteredItems.map(item => (
              <div key={item.id} style={{ ...styles.itemRow, ...(selectedItem?.id === item.id ? styles.selectedRow : {}) }} onClick={() => setSelectedItem(item)}>
                <div style={{ ...styles.avatar, backgroundColor: activeTab === 'products' ? '#FFF7ED' : '#FAF5FF', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1px solid ${activeTab === 'products' ? '#FFEDD5' : '#F3E8FF'}` }}>
                  {(item.photo_url || item.image_path || item.image) ? (
                    <img 
                      src={item.photo_url || item.image_path || item.image} 
                      alt={item.name} 
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                      onError={(e) => { 
                        e.target.style.display = 'none'; 
                        if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <span style={{ color: activeTab === 'products' ? '#EA580C' : '#7C3AED', fontWeight: 'bold', display: (item.photo_url || item.image_path || item.image) ? 'none' : 'flex' }}>
                    {item.name[0]}
                  </span>
                </div>
                <div style={styles.itemInfo}>
                  <div style={styles.itemTop}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={styles.itemName}>{item.name}</span>
                      {(item.is_public === 0 || item.is_public === false || item.is_public === '0') ? (
                        <span style={{ fontSize: '10px', padding: '1px 5px', borderRadius: '4px', backgroundColor: '#F1F5F9', color: '#64748B', fontWeight: '700' }}>
                          🔒 Private
                        </span>
                      ) : (
                        <span style={{ fontSize: '10px', padding: '1px 5px', borderRadius: '4px', backgroundColor: '#DCFCE7', color: '#16A34A', fontWeight: '700' }}>
                          🟢 Online
                        </span>
                      )}
                    </div>
                    <span style={{ ...styles.itemValue, color: (item.opening_stock <= item.low_stock_alert) ? '#DC2626' : '#1E293B' }}>{formatCurrency(item.sale_price)}</span>
                  </div>
                  <div style={styles.itemBottom}>
                    <span style={styles.itemSub}>
                      {activeTab === 'products' 
                        ? `${item.opening_stock || 0} ${item.unit || ''} ${t(LangKeys.stock_in)}` 
                        : `${t(LangKeys.unit)} ${item.unit || ''}`}
                    </span>
                    <ChevronRight size={16} color="#CBD5E1" />
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div style={styles.noDataBox}>{t(LangKeys.bills_no_matching_found)}</div>
          )}
        </div>

        <button style={styles.bottomAddBtn} onClick={handleAddClick}>
          <Plus size={20} /> {activeTab === 'products' ? t(LangKeys.add_product_caps) : t(LangKeys.add_service_caps)}
        </button>
      </div>

      {/* RIGHT COLUMN: DETAILS */}
      <div style={styles.rightCol}>
        {selectedItem ? (
          selectedItem.item_type === 'product' ? (
            <ProductDetailsView 
              item={selectedItem} 
              onEdit={() => handleEditClick(selectedItem)} 
              onDelete={handleDeleteItem}
              onStockUpdated={fetchItems}
              onClose={() => setSelectedItem(null)}
            />
          ) : (
            <ServiceDetailsView 
              item={selectedItem} 
              onEdit={() => handleEditClick(selectedItem)} 
              onDelete={handleDeleteItem}
              onClose={() => setSelectedItem(null)}
              onRefresh={fetchItems}
            />
          )
        ) : (
          <div style={styles.emptyView}>
            <div style={styles.emptyIconBox}><Package size={64} color="#E2E8F0" /></div>
          </div>
        )}
      </div>

      {/* MODALS */}
      <AddProductModal isOpen={showProductModal} onClose={() => setShowProductModal(false)} item={editingItem} onSuccess={handleSaveSuccess} />
      <AddServiceModal isOpen={showServiceModal} onClose={() => setShowServiceModal(false)} item={editingItem} onSuccess={handleSaveSuccess} />

      {/* FILTER MODAL */}
      {showFilterModal && (
        <div style={styles.modalOverlay} onClick={() => setShowFilterModal(false)}>
          <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>{t(LangKeys.filter_and_sort)}</h3>
              <button style={styles.closeBtn} onClick={() => setShowFilterModal(false)}><X size={20} /></button>
            </div>
            <div style={styles.modalBody}>
              <div style={styles.filterSection}>
                <p style={styles.filterSectionTitle}>{t(LangKeys.sort_by).toUpperCase()}</p>
                {['latestFirst', 'oldestFirst', 'byNameAZ', 'byNameZA'].map(opt => (
                  <div key={opt} style={styles.radioOption} onClick={() => setCurrentSort(opt)}>
                    <div style={{ ...styles.radioCircle, borderColor: currentSort === opt ? '#3B82F6' : '#CBD5E1' }}>{currentSort === opt && <div style={styles.radioInner} />}</div>
                    <span style={styles.radioLabel}>{opt === 'latestFirst' ? t(LangKeys.sort_latest_first) : opt === 'oldestFirst' ? t(LangKeys.sort_oldest_first) : opt === 'byNameAZ' ? t(LangKeys.sort_by_name_az) : t(LangKeys.sort_by_name_za)}</span>
                  </div>
                ))}
              </div>
            </div>
            <div style={styles.modalFooter}>
              <button style={styles.clearBtn} onClick={() => { setCurrentSort('latestFirst'); setProductFilter('all'); setShowFilterModal(false); }}>{t(LangKeys.clear_filters)}</button>
              <button style={styles.applyBtn} onClick={() => setShowFilterModal(false)}>{t(LangKeys.apply_filters)}</button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .premium-spinner { width: 30px; height: 30px; border: 3px solid #E2E8F0; border-top: 3px solid #3B82F6; border-radius: 50%; animation: spin 0.8s linear infinite; }
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

const styles = {
  container: { display: 'flex', height: 'calc(100vh - 80px)', backgroundColor: '#F8FAFC', position: 'relative' },
  leftCol: { width: '400px', backgroundColor: '#FFF', borderRight: '1px solid #E2E8F0', display: 'flex', flexDirection: 'column' },
  summarySection: { display: 'flex', padding: '16px', gap: '12px', borderBottom: '1px solid #F1F5F9' },
  summaryCard: { flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' },
  summaryLabel: { fontSize: '10px', color: '#94A3B8', fontWeight: '700' },
  summaryValue: { fontSize: '15px', fontWeight: '800' },
  tabContainer: { display: 'flex', padding: '12px', gap: '8px' },
  tabBtn: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '10px', borderRadius: '12px', border: '1px solid #E2E8F0', backgroundColor: '#FFF', color: '#64748B', fontWeight: '600', fontSize: '13px', cursor: 'pointer' },
  activeTab: { backgroundColor: '#F0F9FF', color: '#3B82F6', borderColor: '#3B82F6' },
  searchRow: { padding: '0 12px 12px 12px', display: 'flex', gap: '8px' },
  searchBox: { flex: 1, display: 'flex', alignItems: 'center', gap: '8px', padding: '0 12px', height: '40px', backgroundColor: '#F8FAFC', borderRadius: '10px', border: '1px solid #E2E8F0' },
  searchInput: { border: 'none', outline: 'none', backgroundColor: 'transparent', fontSize: '13px', width: '100%', fontWeight: '500' },
  filterBtn: { width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '10px', border: '1px solid #E2E8F0', backgroundColor: '#FFF', color: '#64748B', cursor: 'pointer' },
  activeFilter: { backgroundColor: '#3B82F6', color: '#FFF' },
  reportBtn: { width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '10px', border: '1px solid #E2E8F0', backgroundColor: '#FFF', color: '#3B82F6', cursor: 'pointer', transition: 'all 0.2s' },
  listArea: { flex: 1, overflowY: 'auto', padding: '0 8px' },
  itemRow: { display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', borderRadius: '12px', cursor: 'pointer', transition: 'all 0.2s', marginBottom: '4px' },
  selectedRow: { backgroundColor: '#F1F5F9' },
  avatar: { width: '40px', height: '40px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' },
  itemInfo: { flex: 1 },
  itemTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  itemName: { fontSize: '14px', fontWeight: '700', color: '#1E293B' },
  itemValue: { fontSize: '14px', fontWeight: '800' },
  itemBottom: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '2px' },
  itemSub: { fontSize: '11px', color: '#94A3B8', fontWeight: '500' },
  bottomAddBtn: { margin: '12px', height: '48px', backgroundColor: '#3B82F6', color: '#FFF', border: 'none', borderRadius: '14px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer' },
  loadingBox: { display: 'flex', justifyContent: 'center', padding: '40px' },
  noDataBox: { textAlign: 'center', padding: '40px', color: '#94A3B8', fontSize: '13px' },
  rightCol: { flex: 1, backgroundColor: '#FFF', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  emptyView: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '20px', color: '#94A3B8' },
  emptyIconBox: { width: '120px', height: '120px', borderRadius: '40px', backgroundColor: '#FFF', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' },
  emptyTitle: { fontSize: '18px', fontWeight: '700', color: '#64748B', margin: 0 },
  modalOverlay: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modalContent: { width: '100%', maxWidth: '440px', backgroundColor: '#FFF', borderRadius: '24px', padding: '28px', display: 'flex', flexDirection: 'column', gap: '24px', boxShadow: '0 20px 50px rgba(0,0,0,0.15)', animation: 'modalPop 0.25s' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  modalTitle: { fontSize: '20px', fontWeight: '800', color: '#0F172A', margin: 0 },
  closeBtn: { border: 'none', backgroundColor: '#F1F5F9', color: '#64748B', width: '32px', height: '32px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' },
  modalBody: { display: 'flex', flexDirection: 'column', gap: '32px' },
  filterSection: { display: 'flex', flexDirection: 'column', gap: '12px' },
  filterSectionTitle: { fontSize: '11px', fontWeight: '800', color: '#94A3B8', letterSpacing: '0.08em' },
  radioOption: { display: 'flex', alignItems: 'center', gap: '14px', cursor: 'pointer', padding: '10px 12px', borderRadius: '12px' },
  radioCircle: { width: '22px', height: '22px', borderRadius: '50%', border: '2px solid', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  radioInner: { width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#3B82F6' },
  radioLabel: { fontSize: '15px', fontWeight: '600', color: '#1E293B' },
  modalFooter: { display: 'flex', gap: '12px', marginTop: '8px' },
  clearBtn: { flex: 1, height: '48px', backgroundColor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '14px', color: '#64748B', fontWeight: '700', cursor: 'pointer' },
  applyBtn: { flex: 2, height: '48px', backgroundColor: '#3B82F6', border: 'none', borderRadius: '14px', color: '#FFF', fontWeight: '700', cursor: 'pointer' },
};

export default ItemsHome;
